import Phaser from 'phaser';
import { SCENE, GAME_WIDTH, GAME_HEIGHT, placementSize } from '../config.js';
import { GARDEN_DOOR_ZONE, EXIT_DOM_ZONE, HOME_FLOOR_POLYLINE, doorAtFeet, feetRectAt } from './homeLayout.mjs';
import { gameState } from '../state/gameState.js';
import { MoneyBar } from '../ui/MoneyBar.js';
import { attachFriendsButton } from '../ui/FriendsButton.js';
import { SceneTransition } from '../ui/SceneTransition.js';
import { WalkController } from '../ui/WalkController.js';
import { NatalkaSprite } from '../ui/NatalkaSprite.js';
import { MAIN_SPAWN_FROM_HOME } from './mainLayout.js';
import { AnimalsController } from '../ui/AnimalsController.js';
import { openAnimalActions } from '../ui/animalActions.js';
import { PlacementHud } from '../ui/PlacementHud.js';
import { attachPlacementMenu } from '../ui/placementInteraction.js';
import { placementTexture } from '../ui/seating.js';
import { sizePlacement, applyPlacementFlip } from '../ui/placementSizing.js';
import { attachClockHand } from '../ui/clockHand.js';
import { depthSortScene } from '../ui/depthSort.js';
import { fitCharacter, centreForFeet } from '../ui/outdoorScale.mjs';
import { ROOM_PERSPECTIVE } from './roomPerspective.mjs';

import { showModal } from '../ui/Modal.js';
import { showToast } from '../ui/Toast.js';
import { messageFor } from '../api/errors.js';
import { attachCoordsDebug } from '../ui/CoordsDebug.js';
import { attachSceneEditMode } from '../ui/SceneEditMode.js';
import { reportLocation } from '../ui/locationTracker.js';
import { realtime } from '../api/realtime.js';
import { attachRemotePresence } from '../ui/remotePresence.js';
import { FurnitureSeats, placementPairs } from '../ui/FurnitureSeats.js';
import { attachRoomChat } from '../ui/ChatInput.js';
import { attachGardenWindow } from '../ui/GardenWindow.js';
import { queueMissing, loadCtx, prioritiseFor } from '../ui/sceneLoading.js';
import { needsFor } from '../assetManifest.mjs';
import { preloader } from './PreloadScene.js';
import { renderBackground } from '../render/world.js';

// Drzwi (kuchnia/salon/winda) są teraz namalowane w bg_home*.png — nie renderujemy już
// sprite'ów drzwi ani animacji windy. Zostają tylko strefy "wejścia": 2 dolne narożniki +
// wysokość definiują prostokąt kolizji, w który wchodzi Natalka, by przejść do pokoju.
// Kłódka nad strefą oznacza pokój zablokowany.
// Exported so the read-only visitor HomeScene navigates via the SAME door positions
// (keeping visiting consistent with playing your own house).
export const ROOM_DOORS = [
  { roomId: 'kuchnia',       toScene: SCENE.KITCHEN,      bottomL: [299, 490], bottomR: [378, 490], h: 252, zone: { padX: 0 } },
  { roomId: 'salon',         toScene: SCENE.SALON,        bottomL: [ 70, 531], bottomR: [189, 531], h: 335, zone: { padX: 0 } },
  { roomId: 'drugie_pietro', toScene: SCENE.SECOND_FLOOR, bottomL: [590, 462], bottomR: [682, 462], h: 196, zone: { padX: 0 } },
];

// The door zones, the floor line and the patio-door return spawn are pure geometry and live in
// homeLayout.mjs, where a test can reach them. Re-exported here because this is where the
// rest of the game has always imported them from.
export { GARDEN_DOOR_ZONE, EXIT_DOM_ZONE, HOME_FLOOR_POLYLINE };

// Feet collision rect used for door detection — shared with the visitor HomeScene so
// both trigger room entry at exactly the same spots. `feetY` carries the room's perspective.
export function homeFeetRect(natalka) {
  const r = feetRectAt(natalka.x, natalka.feetY ?? natalka.y + 120);
  return new Phaser.Geom.Rectangle(r.x, r.y, r.w, r.h);
}

// What to seed a scene's door cooldown with on arrival: the door you are ALREADY standing in,
// so it does not fire the moment you get there. Coming back from the farm puts your feet on
// the patio door's mat, and without this you are sent straight back out onto the farm.
export function arrivalDoor(natalka) {
  const hit = detectHomeDoor(natalka);
  if (!hit) return null;
  return hit.kind === 'room' ? hit.door : hit.kind;
}


// Returns the door the avatar's feet are currently in, or null.
//   { kind: 'room', door } | { kind: 'garden' } | { kind: 'exit' } | null
export function detectHomeDoor(natalka) {
  return doorAtFeet(feetRectAt(natalka.x, natalka.feetY ?? natalka.y + 120), ROOM_DOORS);
}

export class HomeScene extends Phaser.Scene {
  constructor() { super(SCENE.HOME); }

  preload() {
    // Staged loading: only what this scene draws now, only if the cache lacks it.
    queueMissing(this, needsFor(this.scene.key, loadCtx()));
    prioritiseFor(this.scene.key);
  }

  create(data) {
    if (gameState.raw?.house?.locked) {
      this.scene.start(SCENE.MAIN);
      return;
    }

    // Level-aware bg
    const lvl = gameState.raw?.house?.level ?? 1;
    const bgKey = (lvl > 1 && this.textures.exists(`bg_home_lvl${lvl}`)) ? `bg_home_lvl${lvl}` : 'bg_home';
    this.bg = renderBackground(this, bgKey);

    // Drzwi (i ich wygląd zablokowany/odblokowany) są namalowane w tle — żadnych nakładek.
    // Zostają tylko strefy wejścia; wejście w zablokowany pokój pokaże modal odblokowania.
    this.doors = ROOM_DOORS;

    // Natalka with outfit — smaller and slower towards the back wall (roomPerspective.mjs)
    this.perspective = ROOM_PERSPECTIVE.home;
    // spawnY is a sprite centre (a restored position); spawnFeetY is where the feet go (homeArrival).
    const spawnY = data?.spawnY ?? (data?.spawnFeetY != null ? centreForFeet(this, data.spawnFeetY) : GAME_HEIGHT - 240);
    this.natalka = new NatalkaSprite(this, data?.spawnX ?? GAME_WIDTH / 2, spawnY);
    this.natalka.setOutfit(gameState.raw?.inventory?.equipped);
    // Depth is y-sorted every frame (depthSortScene) so you can stand behind furniture.
    this.walker = new WalkController(this, this.natalka, { floorPolyline: HOME_FLOOR_POLYLINE, onBroadcast: (p) => realtime.move(p) });
    fitCharacter(this);
    reportLocation(this, this.natalka.x, this.natalka.y, { immediate: true });

    // Live farm behind the patio-door glass (the bg's glass pixels are transparent).
    attachGardenWindow(this, { homeBgKey: bgKey, getData: () => gameState.raw });

    this.animals = new AnimalsController(this, {
      sceneId: 'home',
      ownerId: window.__myId ?? null,
      getPlayers: () => {
        const players = [];
        if (window.__myId) players.push({ id: window.__myId, x: this.natalka.x, y: this.natalka.y });
        if (this._presence) for (const [id, av] of this._presence.avatars) players.push({ id, x: av.sprite.x, y: av.sprite.y });
        return players;
      },
    });
    this.events.on('shutdown', () => this.animals.destroy());

    // Render placements (animals are owned by the controller above).
    this.placementSprites = new Map();
    this._renderPlacementsAndAnimals();
    this.furnitureSeats = new FurnitureSeats(this, {
      natalka: this.natalka, walker: this.walker,
      getPlacements: () => placementPairs(this.placementSprites.values()),
      getPeers: () => this._presence?.avatars.values() ?? [],
    });

    // Doors are part of the painted background — no "Wyjdź"/"Ogródek" labels; the doors
    // are obvious. Walking into the door zones still triggers the transition.
    this.transition = new SceneTransition(this);

    new PlacementHud(this);
    new MoneyBar(this);
    attachFriendsButton(this);

    this._exitTriggered = false;
    this._lastTriggeredDoor = arrivalDoor(this.natalka);

    this._unsub = gameState.on(() => this._refreshAll());
    this.events.on('shutdown', () => this._unsub?.());

    attachCoordsDebug(this);
    if (import.meta.env?.DEV) attachSceneEditMode(this);  // dev-only foreground/walk authoring (press E)

    if (window.__myId) this._presence = attachRemotePresence(this, `house:${window.__myId}:${this.scene.key}`);
    if (window.__myId) attachRoomChat(this, `house:${window.__myId}:${this.scene.key}`);
  }

  update(_t, dt) {
    this.walker.update(dt);
    fitCharacter(this);
    this.animals.update(dt);
    depthSortScene(this, { actor: this.natalka, placements: this.placementSprites.values(), presence: this._presence });
    // Seated, her (x, y) is the pose anchor on the piece: save where standing up puts her.
    const at = this.furnitureSeats?.standingPoint() ?? this.natalka;
    reportLocation(this, at.x, at.y);
    if (this.furnitureSeats?.isSeated) return;   // on a sofa / in a bed: no doors
    this.transition.checkExits(this.natalka);
    if (this._exitTriggered) return;

    // Walking-into-room detection (with cooldown for cancelled doors). detectHomeDoor is the
    // SAME test the visitor's house runs — this scene used to carry its own copy, which
    // measured the feet from a fixed offset and so ignored the room's perspective. The two
    // then disagreed by a couple of pixels and a spawn safe in one was inside a door in the other.
    const hit = detectHomeDoor(this.natalka);
    const hitDoor = hit?.kind === 'room' ? hit.door : null;
    const hitGarden = hit?.kind === 'garden';
    const hitExit = hit?.kind === 'exit';

    if (hitDoor) {
      if (this._lastTriggeredDoor && this._lastTriggeredDoor.roomId === hitDoor.roomId) {
        return;
      }
      this._exitTriggered = true;
      this._handleDoorEntry(hitDoor);
      return;
    }

    if (hitGarden) {
      if (this._lastTriggeredDoor === 'garden') return;
      this._exitTriggered = true;
      this.scene.start(SCENE.FARM, { from: 'home' });   // arrive at the farm's house corner
      return;
    }

    if (hitExit) {
      if (this._lastTriggeredDoor === 'exit') return;
      this._exitTriggered = true;
      // Wychodzimy z domu u stóp schodów (przed wejściem).
      this.scene.start(SCENE.MAIN, { spawnX: MAIN_SPAWN_FROM_HOME.x, spawnFeetY: MAIN_SPAWN_FROM_HOME.feetY, spawnFacing: MAIN_SPAWN_FROM_HOME.facing });
      return;
    }

    // Natalka left all door zones → allow re-triggering
    this._lastTriggeredDoor = null;
  }

  _renderPlacementsAndAnimals() {
    const placements = (gameState.raw?.placements ?? []).filter(p => p.scene === 'home');

    for (const p of placements) {
      const tex = placementTexture(p);
      const sprite = this.add.image(p.x, p.y, this.textures.exists(tex) ? tex : 'plot_empty')
        .setInteractive({ useHandCursor: true });
      if (!this.textures.exists(tex)) preloader.whenReady(tex, () => { if (sprite.active) { sprite.setTexture(tex); sizePlacement(sprite, p, this.perspective); } });
      sizePlacement(sprite, p, this.perspective);
      applyPlacementFlip(sprite, p);
      attachClockHand(this, sprite, p);
      attachPlacementMenu(this, sprite, p);
      sprite.placement = p;
      this.placementSprites.set(p.id, sprite);
      if (p.type === 'buda') this.animals.attachBudaClick(sprite, p);
    }
  }

  _refreshAll() {
    for (const sp of this.placementSprites.values()) sp.destroy();
    this.placementSprites.clear();
    this._renderPlacementsAndAnimals();
    this.natalka?.setOutfit(gameState.raw?.inventory?.equipped);
  }

  async _handleDoorEntry(door) {
    const rooms = gameState.raw.house.rooms;
    if (!rooms[door.roomId]) {
      const cat = gameState.raw.catalog.rooms[door.roomId];
      const ok = await showModal(this, {
        title: `Odblokować ${cat.name_pl}?`,
        body: `Koszt: ${cat.price} {coin}\n(Masz: ${gameState.raw.money} {coin})`,
        confirmLabel: 'Odblokuj', cancelLabel: 'Nie',
      });
      if (!ok) {
        this._lastTriggeredDoor = door;
        this._exitTriggered = false;
        return;
      }
      try {
        await gameState.unlockRoom(door.roomId);
        showToast(this, `${cat.name_pl} odblokowane!`);
      } catch (e) {
        showToast(this, messageFor(e));
        this._lastTriggeredDoor = door;
        this._exitTriggered = false;
        return;
      }
    }

    this.scene.start(door.toScene);
  }

  async _onAnimalClick(a, opts = {}) {
    return openAnimalActions(this, a, { ...opts, onPetHeart: () => this.animals.sprites.get(a.id)?.showPetHeart() });
  }
}
